/**
 * Экран `/results/compare?ids=…`: 2–4 готовых изображения рядом (колонки), «Брак» и метка этапа над картинкой,
 * общая таблица-паспорт запуска (различающиеся строки выделены), входы и промпт по колонкам, синхронная лупа:
 * клик по любой картинке увеличивает все в одной относительной точке, Esc или повторный клик — выход.
 */
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Heart, HeartOff, Send, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ErrorLine, Loading } from "@/components/trends/ui";
import { COMPARE_MIN, LOUPE_SCALE, loupeOrigin, parseCompareIds, passportRows } from "@/lib/comparePick";
import { mediaUrl, thumbUrl } from "@/lib/media";
import { loadResultCard, pickResult, resultKeys, resultPath, RESULTS_PREFIX, type ResultCard } from "@/lib/results";
import { likeState } from "@/lib/triptych";
import { enhanceNote, STAGE_HINT, STAGE_LABEL, stageOf } from "@/lib/verdict";
import { OwnerLinks } from "@/pages/ResultsPage";

type Loupe = { fx: number; fy: number } | null;

const STAGE_ICON = { enhance: Sparkles, publish: Send, approve: Heart } as const;
const COLS = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" } as const;

/** Картинка колонки: у видео — постер (кадр 0), иначе сам файл. */
const imagePath = (card: ResultCard): string =>
  card.video?.frames.find((f) => f.ms === 0)?.path ?? card.variant.asset_path;

function Column({
  id,
  card,
  loupe,
  onLoupe,
  busy,
  onPick,
  onSize,
}: {
  id: number;
  card: ResultCard;
  loupe: Loupe;
  onLoupe: (e: React.MouseEvent<HTMLDivElement>) => void;
  busy: boolean;
  onPick: (id: number, status: "picked" | "rejected") => void;
  onSize: (id: number, size: string) => void;
}) {
  const { liked, disliked } = likeState(card.variant.pick);
  const stage = stageOf(card.kind ?? card.generation.kind);
  const StageIcon = STAGE_ICON[stage];
  const heart = "absolute top-2 grid size-8 place-items-center rounded-full bg-black/30 disabled:opacity-60";
  return (
    <div className="grid min-w-0 content-start gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
        <Link to={resultPath(id)} target="_blank" className="text-accent-text hover:underline">
          #{id} · открыть карточку ↗
        </Link>
        <OwnerLinks kind={card.generation.kind} owner={card.owner} />
      </div>
      <div
        className={clsx(
          "relative mx-auto w-fit max-w-full overflow-hidden rounded-[4px] bg-bg-elevated",
          loupe ? "cursor-zoom-out" : "cursor-zoom-in",
        )}
        onClick={onLoupe}
        onMouseMove={loupe ? onLoupe : undefined}
      >
        <img
          src={mediaUrl(imagePath(card))}
          alt=""
          className="block h-auto max-h-[calc(100vh-12rem)] w-auto max-w-full"
          draggable={false}
          onLoad={
            card.video
              ? undefined
              : (e) => onSize(id, `${e.currentTarget.naturalWidth}×${e.currentTarget.naturalHeight}`)
          }
          style={
            loupe
              ? { transform: `scale(${LOUPE_SCALE})`, transformOrigin: `${loupe.fx * 100}% ${loupe.fy * 100}%` }
              : undefined
          }
        />
        <button
          type="button"
          className={clsx(heart, "left-2", disliked ? "text-err" : "text-white")}
          aria-pressed={disliked}
          title="Брак"
          aria-label="Брак"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onPick(id, "rejected");
          }}
        >
          <HeartOff size={20} className={disliked ? "fill-current" : undefined} />
        </button>
        <button
          type="button"
          className={clsx(heart, "right-2", liked ? "text-accent" : "text-white")}
          aria-pressed={liked}
          title={STAGE_HINT[stage]}
          aria-label={STAGE_LABEL[stage]}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onPick(id, "picked");
          }}
        >
          <StageIcon size={20} className={liked ? "fill-current" : undefined} />
        </button>
      </div>
    </div>
  );
}

function Details({ card }: { card: ResultCard }) {
  const g = card.generation;
  return (
    <div className="grid min-w-0 content-start gap-2">
      {g.inputs.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {g.inputs.map((i, n) => (
            <figure key={`${i.asset_id}-${i.role}-${n}`} className="m-0 grid w-20 gap-0.5">
              <img
                src={thumbUrl(i.path)}
                alt=""
                title={i.hint ? `${i.label}: ${i.hint}` : i.label}
                className="h-24 w-20 rounded-[4px] border border-border object-cover"
              />
              <figcaption className="text-[11px] leading-tight text-fg-muted">{i.role}</figcaption>
            </figure>
          ))}
        </div>
      ) : null}
      {g.prompt ? (
        <details>
          <summary className="cursor-pointer text-[12.5px] text-fg-muted">Промпт</summary>
          <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-elevated p-2 text-[12px] text-fg">
            {g.prompt}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

export function ResultsComparePage() {
  const [search] = useSearchParams();
  const ids = parseCompareIds(search.get("ids"));
  const client = useQueryClient();
  const [loupe, setLoupe] = useState<Loupe>(null);
  const [fileSizes, setFileSizes] = useState<Record<number, string>>({});
  const onSize = (id: number, size: string) => setFileSizes((cur) => (cur[id] === size ? cur : { ...cur, [id]: size }));

  const results = useQueries({
    queries: ids.map((id) => ({ queryKey: resultKeys.card(id), queryFn: () => loadResultCard(id) })),
  });

  const pick = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "picked" | "rejected" }) => pickResult(id, status),
    onSuccess: () => client.invalidateQueries({ queryKey: RESULTS_PREFIX }),
  });
  const onPick = (id: number, status: "picked" | "rejected") => {
    if (!pick.isPending) pick.mutate({ id, status });
  };
  const note = enhanceNote(pick.data?.enhance ?? null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLoupe(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (ids.length < COMPARE_MIN) {
    return (
      <div className="grid gap-2 p-5 text-fg">
        <p>Выберите 2–4 изображения в Результатах</p>
        <Link to="/results" className="text-accent-text hover:underline">
          К результатам
        </Link>
      </div>
    );
  }

  const onLoupe = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const next = loupeOrigin(e.clientX - r.left, e.clientY - r.top, r.width, r.height);
    if (e.type === "click") setLoupe((cur) => (cur ? null : next));
    else setLoupe(next);
  };

  const loaded = ids.flatMap((id, n) => {
    const card = results[n]?.data;
    return card ? [{ id, card }] : [];
  });
  const rows = passportRows(loaded.map((l) => l.card), fileSizes);
  const cols = COLS[ids.length as 2 | 3 | 4] ?? COLS[4];

  return (
    <div className="grid gap-4 p-5 text-fg">
      <p className="m-0 text-[12.5px] text-fg-muted">Клик по фото — лупа во всех колонках, Esc — выйти</p>
      <div className={clsx("grid gap-4", cols)}>
        {ids.map((id, n) => {
          const q = results[n];
          if (q?.data) {
            return <Column key={id} id={id} card={q.data} loupe={loupe} onLoupe={onLoupe} busy={pick.isPending} onPick={onPick} onSize={onSize} />;
          }
          return (
            <div key={id} className="grid min-w-0 content-start gap-2">
              <div className="text-[12.5px] text-fg-muted">#{id}</div>
              {q?.error ? <ErrorLine error={q.error} /> : <Loading />}
            </div>
          );
        })}
      </div>
      {pick.error ? <ErrorLine error={pick.error} /> : null}
      {note ? <p className="m-0 text-[12.5px] text-fg-muted">{note}</p> : null}

      {loaded.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-[12.5px]">
            <thead>
              <tr>
                <th className="border-b border-border p-1.5" />
                {loaded.map((l) => (
                  <th key={l.id} className="border-b border-border p-1.5 font-medium">
                    #{l.id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className={row.differs ? "bg-accent/10 font-semibold" : undefined}>
                  <th scope="row" className="border-b border-border p-1.5 font-normal text-fg-muted">
                    {row.label}
                  </th>
                  {row.values.map((v, i) => (
                    <td key={loaded[i]?.id ?? i} className="border-b border-border p-1.5">
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className={clsx("grid gap-4", cols)}>
        {ids.map((id, n) => {
          const card = results[n]?.data;
          return card ? <Details key={id} card={card} /> : <div key={id} />;
        })}
      </div>
    </div>
  );
}
